import { startTransition, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { fetchFileBlob } from '@/shared/lib/download';

const FILE_BLOB_KEYS = {
  blob: (orgId: string, storageKey: string) => ['file-blob', orgId, storageKey] as const,
};

/**
 * Fetches a file blob for in-browser preview and exposes a stable ObjectURL.
 * The ObjectURL is automatically revoked when the blob changes or the
 * component that called this hook unmounts.
 *
 * Pass null for storageKey to skip fetching (e.g. when preview is not supported).
 */
export function useFileBlob(storageKey: string | null) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const {
    data: blob,
    isLoading,
    error,
  } = useQuery({
    queryKey: FILE_BLOB_KEYS.blob(orgId ?? '', storageKey ?? ''),
    queryFn: ({ signal }) => fetchFileBlob(storageKey!, signal),
    enabled: !!orgId && !!storageKey,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      startTransition(() => setObjectUrl(null));
      return;
    }
    const url = URL.createObjectURL(blob);
    startTransition(() => setObjectUrl(url));
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  return { objectUrl, blob, isLoading, error };
}
